import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, DollarSign, Tag, BarChart2, Users, Settings,
  FileText, LogOut, Globe, Zap, ArrowLeft, CreditCard, AlertTriangle,
  Gift, Bell, Mail, UserCheck, ToggleLeft, MessageSquare, Layers, ShieldCheck,
  Brain, Database, Clock, Star,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface NavItem { to: string; label: string; icon: React.ElementType; badge?: number }
interface NavSection { header: string; items: NavItem[] }

/** Admin panel layout, completely separate from client sidebar. */
export function AdminAPLayout() {
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();
  const [newMsgCount,    setNewMsgCount]    = useState(0);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [sectionOrder, setSectionOrder] = useState<string[]>([]);

  // Load admin nav section order from platform_settings
  useEffect(() => {
    supabase.from('platform_settings').select('value').eq('key', 'admin_nav_section_order').maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try {
            const parsed = JSON.parse(String(data.value));
            if (Array.isArray(parsed) && parsed.length > 0) setSectionOrder(parsed);
          } catch { /* keep default */ }
        }
      });
  }, []);

  // Poll unread contact messages count every 60 s
  useEffect(() => {
    async function fetchCount() {
      const { count, error } = await supabase
        .from('contact_messages')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'new');
      if (!error) setNewMsgCount(count ?? 0);
    }
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Poll open support ticket count every 60 s
  useEffect(() => {
    async function fetchTicketCount() {
      const { count, error } = await supabase
        .from('support_tickets')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'open');
      if (!error) setOpenTicketCount(count ?? 0);
    }
    fetchTicketCount();
    const interval = setInterval(fetchTicketCount, 60_000);
    return () => clearInterval(interval);
  }, []);

  const ADMIN_NAV_SECTIONS: NavSection[] = [
    {
      header: 'Geral',
      items: [
        { to: '/admin-ap/dashboard',    label: 'Dashboard',      icon: LayoutDashboard },
      ],
    },
    {
      header: 'Financeiro',
      items: [
        { to: '/admin-ap/pagamentos',   label: 'Pagamentos',     icon: CreditCard },
        { to: '/admin-ap/chargebacks',  label: 'Chargebacks',    icon: AlertTriangle },
        { to: '/admin-ap/cupons',       label: 'Cupons',         icon: Tag },
        { to: '/admin-ap/pricing',      label: 'Produtos',       icon: DollarSign },
        { to: '/admin-ap/gift-cards',   label: 'Gift Cards',     icon: Gift },
        { to: '/admin-ap/plan-access',  label: 'Acesso por Plano', icon: Layers },
        { to: '/admin-ap/costs',        label: 'Custos & Lucro', icon: DollarSign },
      ],
    },
    {
      header: 'Usuários',
      items: [
        { to: '/admin-ap/users',        label: 'Usuários',       icon: Users },
        { to: '/admin-ap/afiliados',       label: 'Afiliados',      icon: UserCheck },
        { to: '/admin-ap/referral-promo', label: 'Promo Indicação', icon: Gift },
        { to: '/admin-ap/mensagens',    label: 'Mensagens & Suporte', icon: MessageSquare, badge: (newMsgCount || 0) + (openTicketCount || 0) },
      ],
    },
    {
      header: 'Plataforma',
      items: [
        { to: '/admin-ap/functions',             label: 'Edge Functions',  icon: Zap },
        { to: '/admin-ap/api-usage',             label: 'Uso da API',     icon: BarChart2 },
        { to: '/admin-ap/alertas',               label: 'Alertas',        icon: Bell },
        { to: '/admin-ap/notificacoes',          label: 'Notificações',   icon: Bell },
        { to: '/admin-ap/monitoring/ai',         label: 'Monit. IA',      icon: Brain },
        { to: '/admin-ap/monitoring/database',   label: 'Banco de Dados', icon: Database },
        { to: '/admin-ap/monitoring/cron',       label: 'Tarefas Agend.', icon: Clock },
      ],
    },
    {
      header: 'Conteúdo',
      items: [
        { to: '/admin-ap/content',      label: 'Blog CMS',       icon: Globe },
      ],
    },
    {
      header: 'Compliance',
      items: [
        { to: '/admin-ap/compliance',   label: 'Compliance LGPD', icon: ShieldCheck },
        { to: '/admin-ap/legal-docs',   label: 'Documentos Legais', icon: FileText },
        { to: '/admin-ap/reports',      label: 'Relatórios',      icon: BarChart2 },
      ],
    },
    {
      header: 'Módulo Astrólogos',
      items: [
        { to: '/admin-ap/astrologos',                  label: 'Todos os Astrólogos', icon: Star },
        { to: '/admin-ap/astrologos/clientes',         label: 'Clientes (todos)',    icon: Users },
        { to: '/admin-ap/astrologos/analises',         label: 'Análises (todas)',    icon: FileText },
        { to: '/admin-ap/astrologos/relatorio',        label: 'Relatório do Módulo', icon: BarChart2 },
        { to: '/admin-ap/astrologos/planos',           label: 'Planos & Preços',     icon: DollarSign },
        { to: '/admin-ap/astrologos/configuracoes',    label: 'Config. do Módulo',  icon: Settings },
      ],
    },
    {
      header: 'Sistema',
      items: [
        { to: '/admin-ap/analytics',    label: 'Analytics',      icon: BarChart2 },
        { to: '/admin-ap/audit',        label: 'Auditoria',      icon: FileText },
        { to: '/admin-ap/feature-flags',label: 'Feature Flags',  icon: ToggleLeft },
        { to: '/admin-ap/menu',         label: 'Menu Lateral',   icon: Layers },
        { to: '/admin-ap/emails',       label: 'Emails',         icon: Mail },
        { to: '/admin-ap/integrations', label: 'Integrações',    icon: Zap },
        { to: '/admin-ap/settings',     label: 'Configurações',  icon: Settings },
      ],
    },
  ];

  async function handleSignOut() {
    await signOut();
    navigate('/');
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: 'var(--bg-base)' }}>
      {/* Admin sidebar */}
      <aside style={{
        width: '220px', flexShrink: 0,
        backgroundColor: 'var(--bg-surface)',
        borderRight: '1px solid var(--border-subtle)',
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '1.25rem 1rem', borderBottom: '1px solid var(--border-subtle)' }}>
          <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--petrol)', fontSize: '1.125rem', margin: 0 }}>
            ✦ Admin
          </p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0.25rem 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {profile?.email}
          </p>
        </div>

        {/* Nav */}
        <nav aria-label="Navegação do painel admin" style={{ flex: 1, padding: '0.5rem 0.75rem', display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto' }}>
          {(sectionOrder.length > 0
            ? [...ADMIN_NAV_SECTIONS].sort((a, b) => {
                const ai = sectionOrder.indexOf(a.header);
                const bi = sectionOrder.indexOf(b.header);
                const an = ai === -1 ? sectionOrder.length : ai;
                const bn = bi === -1 ? sectionOrder.length : bi;
                return an - bn;
              })
            : ADMIN_NAV_SECTIONS
          ).map(section => (
            <div key={section.header} style={{ marginBottom: '0.5rem' }}>
              <p style={{
                fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: 'var(--text-muted)',
                padding: '0.5rem 0.5rem 0.25rem', margin: 0,
              }}>
                {section.header}
              </p>
              {section.items.map(({ to, label, icon: Icon, badge }) => (
                <NavLink key={to} to={to}
                  className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
                  style={{ fontSize: 'var(--text-caption)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon size={15} /> {label}
                  </span>
                  {badge && badge > 0 ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: '18px', height: '18px', padding: '0 4px',
                      backgroundColor: '#F5C842', color: '#0C0B0F',
                      fontSize: '10px', fontWeight: 700, borderRadius: '999px',
                    }}>
                      {badge > 99 ? '99+' : badge}
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding: '0.75rem', borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <Link to="/dashboard"
            className="sidebar-item"
            aria-label="Voltar ao aplicativo"
            style={{ fontSize: 'var(--text-caption)', width: '100%', textDecoration: 'none' }}>
            <ArrowLeft size={15} /> Voltar ao App
          </Link>
          <button className="sidebar-item" style={{ width: '100%', fontSize: 'var(--text-caption)' }} onClick={handleSignOut} aria-label="Sair da conta">
            <LogOut size={15} /> Sair
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, overflow: 'auto', padding: '2rem', maxWidth: '1200px' }}>
        <Outlet />
      </main>
    </div>
  );
}
